import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import LanguageSelector from '../components/LanguageSelector';
import { AccountService } from '../services/accountService';
import { isAdminEmail } from '../utils/adminConfig';
import {
  User,
  Mail,
  Globe,
  Bell,
  Key,
  CreditCard,
  Trash2,
  Shield,
  FileText,
  LogOut,
  Edit3
} from 'lucide-react';

const SettingsPage: React.FC = () => {
  const { t } = useTranslation();
  const { user, logout, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [pushNotifications, setPushNotifications] = useState(false);
  const [loading, setLoading] = useState(false);
  const [deletingAccount, setDeletingAccount] = useState(false);

  const handleLogout = async () => {
    try {
      setLoading(true);
      await logout();
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setLoading(false);
    }
  };

  // 계정 삭제 함수
  const handleDeleteAccount = async () => {
    if (!user) {
      alert(t('settings.userNotFound', '사용자를 찾을 수 없습니다.'));
      return;
    }

    // 첫 번째 확인
    const firstConfirm = window.confirm(
      t('settings.deleteAccountWarning',
        '⚠️ 경고: 이 작업은 되돌릴 수 없습니다.\n\n다음 데이터가 영구적으로 삭제됩니다:\n• 모든 명함 데이터\n• 모든 청구서\n• 프로필 정보\n• 업로드된 이미지\n• 계정 정보\n\n계정을 삭제하시겠습니까?')
    );

    if (!firstConfirm) return;

    // 두 번째 확인
    const secondConfirm = window.confirm(
      t('settings.deleteAccountConfirmMessage',
        '최종 확인입니다. 계정이 삭제되면 모든 데이터는 영구적으로 손실됩니다.\n\n계속하시겠습니까?')
    );

    if (!secondConfirm) return;

    // 계정 삭제 실행 (재인증은 deleteAccount 내부에서 처리)
    await executeAccountDeletion();
  };

  const executeAccountDeletion = async () => {
    try {
      setDeletingAccount(true);
      console.log('🗑️ [SettingsPage] Starting account deletion...');

      // Cloud Function을 통해 계정 삭제
      const result = await AccountService.deleteAccount();

      if (result.success) {
        console.log('✅ [SettingsPage] Account deletion successful:', result);

        // 성공 메시지 표시
        alert(t('settings.deleteAccountSuccessMessage', '계정 및 모든 데이터가 성공적으로 삭제되었습니다.'));

        // 로그인 페이지로 리다이렉트
        navigate('/login', { replace: true });
      } else {
        throw new Error(result.message || 'Account deletion failed');
      }

    } catch (error: any) {
      console.error('❌ [SettingsPage] Account deletion failed:', error);

      let errorMessage = t('settings.deleteAccountErrorMessage', '계정 삭제 중 오류가 발생했습니다.');

      const msg = String(error?.message || '');
      if (msg.includes('requires-recent-login') || msg.includes('functions/unauthenticated') || msg.includes('User must be authenticated')) {
        errorMessage = t('settings.deleteAccountRecentLoginRequired', '보안상의 이유로 최근에 로그인한 사용자만 계정을 삭제할 수 있습니다. 로그아웃 후 다시 로그인해주세요.');
      } else if (msg.includes('permission-denied')) {
        errorMessage = t('settings.deleteAccountPermissionDenied', '계정 삭제 권한이 없습니다. 고객 지원팀에 문의해주세요.');
      }

      alert(errorMessage);
    } finally {
      setDeletingAccount(false);
    }
  };

  // 인증 로딩 중일 때 로딩 화면 표시
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading', '로딩 중...')}</p>
        </div>
      </div>
    );
  }

  // 사용자가 로그인하지 않은 경우 로그인 페이지로 리다이렉트
  if (!user) {
    navigate('/login');
    return null;
  }

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Page Title */}
      <div className="bg-white shadow-sm border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <h1 className="text-3xl font-bold text-gray-900">{t('settings.title', '설정')}</h1>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        <div className="space-y-8">

          {/* 🛡️ 관리자 전용 - 사용자 플랜 관리 */}
          {isAdminEmail(user?.email) && (
            <div className="bg-white rounded-lg shadow-sm border-2 border-amber-300 p-6">
              <h2 className="text-lg font-semibold text-gray-900 mb-4">🛡️ 관리자</h2>
              <button
                onClick={() => navigate('/admin')}
                className="w-full flex items-center justify-between p-4 bg-amber-50 rounded-lg hover:bg-amber-100 transition-colors"
              >
                <span className="text-gray-800 font-medium">사용자 플랜 관리</span>
                <span className="text-gray-500">→</span>
              </button>
            </div>
          )}

          {/* Profile Information Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.profileInfo', '프로필 정보')}</h2>
            <div className="space-y-4">
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <User className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-700">{t('settings.nameLabel', '이름')}</span>
                </div>
                <div className="flex items-center space-x-2">
                  <span className="text-gray-900 font-medium">
                    {user?.displayName || t('settings.nameNotSet', '이름이 설정되지 않음')}
                  </span>
                  <button className="p-1 text-[#96742f] hover:text-[#7a5d24]">
                    <Edit3 className="h-4 w-4" />
                  </button>
                </div>
              </div>
              <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-gray-500" />
                  <span className="text-gray-700">{t('settings.emailLabel', '이메일')}</span>
                </div>
                <span className="text-gray-900 font-medium">
                  {user?.email || t('settings.emailNotSet', '이메일이 설정되지 않음')}
                </span>
              </div>
            </div>
          </div>

          {/* Language Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.languageSection', '언어')}</h2>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div className="flex items-center space-x-3">
                <Globe className="h-5 w-5 text-gray-500" />
                <span className="text-gray-700">{t('settings.languageSection', '언어')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <LanguageSelector variant="button" />
              </div>
            </div>
          </div>

          {/* Notifications Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.notificationsSection', '알림')}</h2>
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <Bell className="h-5 w-5 text-gray-500" />
                <span className="text-gray-700">{t('settings.pushNotifications')}</span>
              </div>
              <button
                onClick={() => setPushNotifications(!pushNotifications)}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                  pushNotifications ? 'bg-gray-900' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                    pushNotifications ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>
          </div>

          {/* Account Section */}
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">{t('settings.accountSection', '계정')}</h2>
            <div className="space-y-3">
              <button
                onClick={() => alert(t('settings.changePasswordButton', '비밀번호 변경 기능은 준비 중입니다.'))}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Key className="h-5 w-5 text-[#96742f]" />
                  <span className="text-gray-700">{t('settings.changePasswordButton', '비밀번호 변경')}</span>
                </div>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>

              <a
                href="https://scanvcf.com/subscription-management"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <CreditCard className="h-5 w-5 text-[#96742f]" />
                  <span className="text-gray-700">{t('settings.subscriptionManagement', '구독 관리')}</span>
                </div>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <button
                onClick={handleDeleteAccount}
                disabled={deletingAccount}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center space-x-3">
                  {deletingAccount ? (
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-red-600"></div>
                  ) : (
                    <Trash2 className="h-5 w-5 text-red-600" />
                  )}
                  <span className="text-red-600">
                    {deletingAccount
                      ? t('settings.deleteAccountProgress', '삭제 진행 중...')
                      : t('settings.deleteAccount', '계정 삭제')
                    }
                  </span>
                </div>
                {!deletingAccount && (
                  <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                )}
              </button>

              <a
                href="/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Shield className="h-5 w-5 text-[#96742f]" />
                  <span className="text-gray-700">{t('landing.footer.privacy')}</span>
                </div>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <a
                href="/terms-of-service"
                target="_blank"
                rel="noopener noreferrer"
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <FileText className="h-5 w-5 text-[#96742f]" />
                  <span className="text-gray-700">{t('settings.terms', '이용약관')}</span>
                </div>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </a>

              <button
                onClick={handleLogout}
                disabled={loading}
                className="w-full flex items-center justify-between p-4 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors border border-red-200 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <div className="flex items-center space-x-3">
                  <LogOut className="h-5 w-5 text-red-600" />
                  <span className="text-red-600">
                    {loading ? t('common.loading', '로딩 중...') : t('settings.signOutButton', '로그아웃')}
                  </span>
                </div>
                <svg className="h-4 w-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SettingsPage;
